import { useEffect, useState } from 'react';
import { Fetch } from '../helpers/fetchWrapper';
import { useCancellablePromise } from '../helpers/promiseHandler';

const STATUS = {
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
};

const adminDashboardService = {
  // Prefer dedicated dashboard API when set; fall back to auth host analytics routes.
  _url: process.env.REACT_APP_DASHBOARD_API_URL || process.env.REACT_APP_AUTH_API_URL,

  _analyticsPath(suffix) {
    const base = this._url || '';
    if (/\/dashboard\/?$/.test(base) || base.includes('/api/v1/dashboard')) {
      return `${base.replace(/\/$/, '')}/${suffix}`;
    }
    return `${base.replace(/\/$/, '')}/api/analytics/${suffix}`;
  },

  GetTotalUsers(searchQuery, fetch) {
    const [data, setData] = useState([]);
    const { cancellablePromise } = useCancellablePromise();
    const [status, setStatus] = useState(STATUS.LOADING);
    useEffect(() => {
      setStatus(STATUS.LOADING);
      cancellablePromise(this.getTotalUsers(searchQuery))
        .then(res => {
          setData(() => res);
          setStatus(STATUS.SUCCESS);
        })
        .catch(() => setStatus(STATUS.ERROR));
    }, [fetch]);

    return {
      _loading: status === STATUS.LOADING,
      _error: status === STATUS.ERROR,
      _data: data,
    };
  },

  async getTotalUsers(payload) {
    return await Fetch.post(this._analyticsPath('total-users'), payload);
  },

  GetActiveUsers(searchQuery, fetch) {
    const [data, setData] = useState([]);
    const { cancellablePromise } = useCancellablePromise();
    const [status, setStatus] = useState(STATUS.LOADING);
    useEffect(() => {
      setStatus(STATUS.LOADING);
      cancellablePromise(this.getActiveUsers(searchQuery))
        .then(res => {
          setData(() => res);
          setStatus(STATUS.SUCCESS);
        })
        .catch(() => setStatus(STATUS.ERROR));
    }, [fetch]);

    return {
      _active_users_loading: status === STATUS.LOADING,
      _active_users_error: status === STATUS.ERROR,
      _active_users_data: data,
    };
  },

  async getActiveUsers(payload) {
    return await Fetch.post(this._analyticsPath('active-users'), payload);
  },

  GetTotalRequests(searchQuery, fetch) {
    const [data, setData] = useState([]);
    const { cancellablePromise } = useCancellablePromise();
    const [status, setStatus] = useState(STATUS.LOADING);
    useEffect(() => {
      setStatus(STATUS.LOADING);
      cancellablePromise(this.getTotalRequests(searchQuery))
        .then(res => {
          setData(() => res);
          setStatus(STATUS.SUCCESS);
        })
        .catch(() => setStatus(STATUS.ERROR));
    }, [fetch]);

    return {
      total_requests_loading: status === STATUS.LOADING,
      total_requests_error: status === STATUS.ERROR,
      total_requests_data: data,
    };
  },

  async getTotalRequests(payload) {
    return await Fetch.post(this._analyticsPath('total-requests'), payload);
  },

  GetTotalErrors(searchQuery, fetch) {
    const [data, setData] = useState([]);
    const { cancellablePromise } = useCancellablePromise();
    const [status, setStatus] = useState(STATUS.LOADING);
    useEffect(() => {
      setStatus(STATUS.LOADING);
      cancellablePromise(this.getTotalErrors(searchQuery))
        .then(res => {
          setData(() => res);
          setStatus(STATUS.SUCCESS);
        })
        .catch(() => setStatus(STATUS.ERROR));
    }, [fetch]);

    return {
      total_errors_loading: status === STATUS.LOADING,
      total_errors_error: status === STATUS.ERROR,
      total_errors_data: data,
    };
  },

  async getTotalErrors(payload) {
    return await Fetch.post(this._analyticsPath('total-errors'), payload);
  },

  GetHourlyRequests(searchQuery, fetch) {
    const [data, setData] = useState([]);
    const { cancellablePromise } = useCancellablePromise();
    const [status, setStatus] = useState(STATUS.LOADING);
    useEffect(() => {
      setStatus(STATUS.LOADING);
      cancellablePromise(this.getHourlyRequests(searchQuery))
        .then(res => {
          setData(() => res);
          setStatus(STATUS.SUCCESS);
        })
        .catch(() => setStatus(STATUS.ERROR));
    }, [fetch]);

    return {
      hourly_requests_loading: status === STATUS.LOADING,
      hourly_requests_error: status === STATUS.ERROR,
      hourly_requests_data: data,
    };
  },

  async getHourlyRequests(payload) {
    return await Fetch.post(this._analyticsPath('hourly-requests'), payload);
  },

  GetTopAgents(searchQuery, fetch) {
    const [data, setData] = useState([]);
    const { cancellablePromise } = useCancellablePromise();
    const [status, setStatus] = useState(STATUS.LOADING);
    useEffect(() => {
      setStatus(STATUS.LOADING);
      cancellablePromise(this.getTopAgents(searchQuery))
        .then(res => {
          setData(() => res);
          setStatus(STATUS.SUCCESS);
        })
        .catch(() => setStatus(STATUS.ERROR));
    }, [fetch]);

    return {
      top_agents_loading: status === STATUS.LOADING,
      top_agents_error: status === STATUS.ERROR,
      top_agents_data: data,
    };
  },

  async getTopAgents(payload) {
    return await Fetch.post(this._analyticsPath('top-agents'), payload);
  },

  GetPlatforms(searchQuery, fetch) {
    const [data, setData] = useState([]);
    const { cancellablePromise } = useCancellablePromise();
    const [status, setStatus] = useState(STATUS.LOADING);
    useEffect(() => {
      setStatus(STATUS.LOADING);
      cancellablePromise(this.getPlatforms(searchQuery))
        .then(res => {
          setData(() => res);
          setStatus(STATUS.SUCCESS);
        })
        .catch(() => setStatus(STATUS.ERROR));
    }, [fetch]);

    return {
      platforms_loading: status === STATUS.LOADING,
      platforms_error: status === STATUS.ERROR,
      platforms_data: data,
    };
  },

  async getPlatforms() {
    return await Fetch.get(this._analyticsPath('platforms'));
  },
};

export default adminDashboardService;
